import {
  afterRenderEffect,
  AfterRenderRef,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  Signal,
  signal,
  viewChildren,
  WritableSignal,
} from '@angular/core';
import { MatButton } from '@angular/material/button';
import { ImageComponent, SliderComponent, SliderSlideDirective } from '@libs/common/components';
import { IconComponent } from '@libs/components/icons';
import { KeyFigureCardSliderContentModel } from '@libs/cms/models';
import { ContentLinkDirective, HtmlDataLinkDirective } from '@libs/shared/ui';
import { HeadlinesComponent } from '../../items';
import { ContentBaseComponent } from '../content-base.component';

@Component({
  selector: 'ui-key-figure-card-slider',
  templateUrl: './key-figure-card-slider.component.html',
  styleUrls: [
    './key-figure-card-slider.component.scss',
    './key-figure-card-slider.component.lekkerland-2026.scss',
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ImageComponent,
    IconComponent,
    HeadlinesComponent,
    SliderComponent,
    SliderSlideDirective,
    ContentLinkDirective,
    HtmlDataLinkDirective,
    MatButton,
  ],
})
export class KeyFigureCardSliderComponent extends ContentBaseComponent<KeyFigureCardSliderContentModel> {
  private readonly headlineRefs: Signal<readonly ElementRef<HTMLElement>[]> = viewChildren<
    HeadlinesComponent,
    ElementRef<HTMLElement>
  >('headline', { read: ElementRef });

  private readonly headlineMinHeightState: WritableSignal<number> = signal<number>(0);

  private readonly headlineHeightRenderEffect: AfterRenderRef = afterRenderEffect({
    read: onCleanup => {
      const headlineElements: HTMLElement[] = this.headlineRefs()
        .map(headlineRef => headlineRef.nativeElement.querySelector<HTMLElement>('h2'))
        .filter(headline => !!headline);

      if (!headlineElements.length || typeof ResizeObserver === 'undefined') {
        this.headlineMinHeightState.set(0);

        return;
      }

      const resizeObserver: ResizeObserver = new ResizeObserver(() => {
        this.updateHeadlineMinHeight(headlineElements);
      });

      headlineElements.forEach(headline => {
        resizeObserver.observe(headline);
      });

      this.updateHeadlineMinHeight(headlineElements);

      onCleanup(() => {
        resizeObserver.disconnect();
      });
    },
  });

  public readonly headlineMinHeight: Signal<number> = this.headlineMinHeightState.asReadonly();

  public readonly hasAnyButton: Signal<boolean> = computed(() =>
    this.data().cards.some(card => !!card.button?.link?.label),
  );

  private updateHeadlineMinHeight(headlineElements: HTMLElement[]): void {
    const maxHeadlineHeight: number = Math.max(
      0,
      ...headlineElements.map(headline => headline.offsetHeight),
    );

    this.headlineMinHeightState.set(maxHeadlineHeight);
  }
}
